import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAbandonedCarts, getSuggestions, browseCatalog, markSent, unmarkSent,
} from '../api/messaging';
import { useCategories, useSizes, getUseCases } from '../api/meta';
import { cldThumb } from '../utils/img';
import skipzyBadge from '../assets/skipzy-badge.png';

const MAX_SELECT = 5;

// Cloudinary: force-download the full-res original (fl_attachment).
function downloadUrl(url) {
  if (!url) return url;
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/fl_attachment/');
  }
  return url;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// Sequentially download every chosen image. Uses the pre-rendered overlay blob
// when available, else fetches the raw image as a blob (cross-origin <a download>
// only ever fires once — blobs are same-origin so each reliably downloads).
// The browser may ask once to allow multiple downloads.
async function downloadAllImages(designs, previews, overlayOn) {
  for (const d of designs) {
    if (!d.image_url_1) continue;
    const name = `${d.design_code || 'design'}.jpg`;
    const pv = previews[d.design_id];
    if (overlayOn && pv && pv.blob) { saveBlob(pv.blob, name); }
    else {
      try {
        const r = await fetch(downloadUrl(d.image_url_1));
        saveBlob(await r.blob(), name);
      } catch {
        const a = document.createElement('a');       // last-resort direct link
        a.href = downloadUrl(d.image_url_1); a.download = name; a.target = '_blank';
        document.body.appendChild(a); a.click(); a.remove();
      }
    }
    await new Promise(res => setTimeout(res, 350));
  }
}

// Our Price = the lower of ZRP and the (raw) saver price.
function ourPrice(d) {
  const z = Number(d.zoddle_price);
  const s = d.saver_price != null ? Number(d.saver_price) : null;
  return (s != null && s > 0 && s < z) ? s : z;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';   // needed so the canvas isn't tainted (Cloudinary sends CORS)
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

const OVERLAY_FONT = '"Poppins", "Segoe UI", system-ui, -apple-system, sans-serif';
const OVERLAY_ACCENT = '#DE5D26';        // Skipzy brand orange (matches the logo bg)
const OVERLAY_FONT_SCALE = 0.058;
const OVERLAY_LOGO_SCALE = 0.24;         // badge width as a fraction of image width

// Load the Skipzy badge once (bundled asset -> same-origin -> no canvas taint).
let _badgePromise = null;
function getBadge() {
  if (!_badgePromise) {
    _badgePromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = skipzyBadge;
    });
  }
  return _badgePromise;
}

// Ensure the Poppins weights we draw with are loaded before rendering to canvas.
async function ensureOverlayFont() {
  if (!document.fonts) return;
  try { await Promise.all(['500', '600', '700'].map(w => document.fonts.load(`${w} 40px "Poppins"`))); } catch {}
}

function roundRect(ctx, x, y, w, h, r) {
  if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return; }
  ctx.beginPath(); ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
function strikeLine(ctx, xRight, y, w, lw) { ctx.lineWidth = lw; ctx.beginPath(); ctx.moveTo(xRight - w, y); ctx.lineTo(xRight, y); ctx.stroke(); }

// Composite brand (bottom-left) + price (orange pill, bottom-right, over a soft
// scrim) onto the image. MRP is struck above the pill, and hidden when it's
// within 10% of Our Price. Returns { dataUrl, blob }; throws if the source can't
// be drawn cross-origin (non-Cloudinary raw URLs). Keep in sync with the
// approved preview (scrim + price pill variant).
async function renderOverlay(d) {
  const [img, badge] = await Promise.all([loadImage(cldThumb(d.image_url_1, 900)), getBadge()]);
  await ensureOverlayFont();
  const W = img.naturalWidth || 900;
  const H = img.naturalHeight || 900;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);

  const base = Math.round(W * OVERLAY_FONT_SCALE);
  const scrimH = Math.round(base * 3.6);
  const g = ctx.createLinearGradient(0, H - scrimH, 0, H);
  g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,0.60)');
  ctx.fillStyle = g; ctx.fillRect(0, H - scrimH, W, scrimH);

  const padX = Math.round(W * 0.045), padB = Math.round(base * 0.85);
  const our = Math.round(ourPrice(d)), mrp = Math.round(Number(d.mrp));
  const showMrp = mrp > our * 1.10;

  // price pill (bottom-right)
  ctx.font = `700 ${Math.round(base * 1.0)}px ${OVERLAY_FONT}`;
  const pt = `₹${our}`, pw = ctx.measureText(pt).width;
  const pillH = Math.round(base * 1.7), pillW = Math.round(pw + base * 1.2);
  const pillX = W - padX - pillW, pillY = H - padB - pillH;
  ctx.fillStyle = OVERLAY_ACCENT; roundRect(ctx, pillX, pillY, pillW, pillH, Math.round(pillH * 0.30)); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(pt, pillX + pillW / 2, pillY + pillH * 0.55);

  // MRP struck above the pill
  if (showMrp) {
    const sm = Math.round(base * 0.55);
    ctx.font = `500 ${sm}px ${OVERLAY_FONT}`;
    ctx.textAlign = 'right'; ctx.textBaseline = 'alphabetic'; ctx.fillStyle = 'rgba(255,255,255,0.92)';
    const mt = `MRP ₹${mrp}`, my = pillY - Math.round(base * 0.35);
    ctx.fillText(mt, W - padX, my);
    ctx.strokeStyle = 'rgba(255,255,255,0.92)';
    strikeLine(ctx, W - padX, my - sm * 0.32, ctx.measureText(mt).width, Math.max(1, Math.round(sm * 0.08)));
  }

  // brand (bottom-left)
  ctx.fillStyle = '#fff'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.font = `600 ${Math.round(base * 0.74)}px ${OVERLAY_FONT}`;
  ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 5;
  ctx.fillText(d.brand_name || '', padX, pillY + pillH * 0.55);
  ctx.shadowBlur = 0;

  // skipzy logo badge (top-left)
  if (badge) {
    const lw = Math.round(W * OVERLAY_LOGO_SCALE);
    const lh = Math.round(lw * (badge.naturalHeight / badge.naturalWidth));
    const pad = Math.round(W * 0.04);
    ctx.drawImage(badge, pad, pad, lw, lh);
  }

  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
  return { dataUrl, blob };
}

function buildMessage(cart) {
  const useWord = (cart.occasions && cart.occasions[0]) || 'kids';
  return (
    `Hi! 👋 Your Skipzy cart for ${useWord} outfits is still waiting!\n\n` +
    `Book now and we'll bring 25+ outfits to your doorstep — try them at home. ` +
    `No delivery charges, no obligation to buy!\n\n` +
    `Use GET10 for flat 10% off!\n\n` +
    `Book for free on book.skipzy.com\n\n` +
    `Here are a few designs that could be coming your way!`
  );
}

function Pill({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600', green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700', blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
  };
  return <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function DesignCard({ d, selected, onToggle, disabled }) {
  return (
    <button type="button" onClick={() => onToggle(d)} disabled={disabled && !selected}
      className={`relative text-left rounded-lg border-2 overflow-hidden transition
        ${selected ? 'border-zoddle-green ring-2 ring-zoddle-green' : 'border-gray-200 hover:border-gray-300'}
        ${disabled && !selected ? 'opacity-40 cursor-not-allowed' : ''}`}>
      {selected && <span className="absolute top-1 right-1 bg-zoddle-green text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center z-10">✓</span>}
      <div className="aspect-square bg-gray-50">
        {d.image_url_1
          ? <img src={cldThumb(d.image_url_1, 300)} alt={d.design_name} className="w-full h-full object-cover" loading="lazy" />
          : <div className="w-full h-full flex items-center justify-center text-gray-300 text-[10px]">no image</div>}
      </div>
      <div className="p-1.5">
        <div className="text-[11px] font-semibold text-gray-800 truncate">{d.brand_name || '—'}</div>
        <div className="text-[10px] text-gray-500 truncate">{d.design_name}</div>
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[11px] font-bold text-gray-900">₹{d.zoddle_price}</span>
          <span className="text-[9px] text-gray-400">{d.category}</span>
        </div>
      </div>
    </button>
  );
}

// ── Picker modal ─────────────────────────────────────────────────────────────
function PickerModal({ cart, onClose, onSent }) {
  const categories = useCategories();
  const sizes = useSizes();
  const [useCases, setUseCases] = useState([]);
  useEffect(() => { getUseCases().then(setUseCases).catch(() => {}); }, []);

  const [childIdx, setChildIdx] = useState(0);
  const [tab, setTab] = useState('suggested');
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState(buildMessage(cart));
  const [copied, setCopied] = useState('');
  const [sentAt, setSentAt] = useState(cart.sent_at);
  const [overlay, setOverlay] = useState(true);
  const [previews, setPreviews] = useState({});   // design_id -> {dataUrl, blob} | {failed:true}
  const [rendering, setRendering] = useState(false);

  const child = cart.children[childIdx] || cart.children[0];

  // (Re)generate brand+price overlays whenever the selection or toggle changes.
  useEffect(() => {
    if (!overlay) { setPreviews({}); return; }
    let cancelled = false;
    (async () => {
      setRendering(true);
      const next = {};
      for (const d of selected) {
        if (!d.image_url_1) continue;
        try { next[d.design_id] = await renderOverlay(d); }
        catch { next[d.design_id] = { failed: true }; }
      }
      if (!cancelled) { setPreviews(next); setRendering(false); }
    })();
    return () => { cancelled = true; };
  }, [selected, overlay]);

  const [sugg, setSugg] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const [bItems, setBItems] = useState([]);
  const [bTotal, setBTotal] = useState(0);
  const [bLoading, setBLoading] = useState(false);
  const [bf, setBf] = useState({ q: '', gender: child?.gender || '', size: '', category: '', use_case: '', brand: '' });

  const loadSuggestions = useCallback(async () => {
    if (!child) return;
    setSuggLoading(true);
    try {
      const res = await getSuggestions({
        sizes: child.sizes, genders: [child.gender].filter(Boolean),
        categories: cart.categories, use_cases: cart.use_cases,
        brands: (cart.brands || '').split(',').map(s => s.trim()).filter(Boolean), limit: 20,
      });
      setSugg(res.designs || []);
    } finally { setSuggLoading(false); }
  }, [child, cart]);
  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const loadBrowse = useCallback(async (reset = true) => {
    setBLoading(true);
    try {
      const offset = reset ? 0 : bItems.length;
      const res = await browseCatalog({ ...bf, offset, limit: 40 });
      setBTotal(res.total || 0);
      setBItems(reset ? (res.designs || []) : [...bItems, ...(res.designs || [])]);
    } finally { setBLoading(false); }
  }, [bf, bItems]);
  useEffect(() => { if (tab === 'browse') loadBrowse(true); /* eslint-disable-next-line */ }, [tab, bf.gender, bf.size, bf.category, bf.use_case]);

  const selectedIds = useMemo(() => new Set(selected.map(d => d.design_id)), [selected]);
  const toggle = (d) => setSelected(prev =>
    prev.find(x => x.design_id === d.design_id)
      ? prev.filter(x => x.design_id !== d.design_id)
      : (prev.length >= MAX_SELECT ? prev : [...prev, d]));

  const flash = (k) => { setCopied(k); setTimeout(() => setCopied(''), 1500); };
  const copyMessage = async () => { try { await navigator.clipboard.writeText(message); flash('msg'); } catch {} };
  const copyPhone = async () => { try { await navigator.clipboard.writeText(cart.phone); flash('phone'); } catch {} };
  const doMarkSent = async () => {
    const res = await markSent({ phone: cart.phone, design_codes: selected.map(d => d.design_code).filter(Boolean), message });
    setSentAt(res.sent_at); onSent(cart.phone, res.sent_at, res.sent_by); flash('sent');
  };
  const sendWhatsApp = () => {
    const num = '91' + (cart.phone || '').replace(/\D/g, '').slice(-10);
    // web.whatsapp.com forces WhatsApp Web in the browser (vs wa.me handing off
    // to the desktop app).
    window.open(`https://web.whatsapp.com/send?phone=${num}&text=${encodeURIComponent(message)}`, '_blank');
  };
  const downloadImages = () => downloadAllImages(selected, previews, overlay);

  const grid = (items) => (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {items.map(d => <DesignCard key={d.design_id} d={d} selected={selectedIds.has(d.design_id)} onToggle={toggle} disabled={selected.length >= MAX_SELECT} />)}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl my-4">
        {/* header */}
        <div className="flex items-start justify-between p-4 border-b sticky top-0 bg-white rounded-t-xl z-20">
          <div>
            <div className="text-base font-bold text-gray-900 flex items-center gap-2">
              {cart.name || cart.phone}
              {sentAt && <Pill tone="green">Sent · {fmtDate(sentAt)}</Pill>}
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
              <span>{cart.phone}</span>
              {cart.pincode && <Pill tone={cart.serviceable ? 'green' : 'red'}>{cart.pincode}{cart.serviceable ? '' : ' · Unserviceable'}</Pill>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div className="p-4 space-y-4">
          {/* actual preferences */}
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700 space-y-0.5">
            <div><span className="text-gray-400">Size:</span> {(child?.gender || '?')} · {child?.age_label} ({(child?.sizes || []).join(', ')})</div>
            {cart.styles_raw && <div><span className="text-gray-400">Style:</span> {cart.styles_raw}</div>}
            {cart.brands && <div><span className="text-gray-400">Brands:</span> {cart.brands}</div>}
            {cart.price_range && <div><span className="text-gray-400">Budget:</span> {cart.price_range}</div>}
            <div><span className="text-gray-400">Inferred:</span> {cart.occasions.join(', ') || '—'}{cart.categories.length ? ` · ${cart.categories.join(', ')}` : ''}</div>
          </div>

          {/* child selector */}
          {cart.children.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Child:</span>
              {cart.children.map((c, i) => (
                <button key={i} onClick={() => setChildIdx(i)}
                  className={`text-xs px-3 py-1 rounded-full border ${i === childIdx ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-300 text-gray-600'}`}>
                  {c.gender || '?'} · {c.age_label}
                </button>
              ))}
            </div>
          )}

          {/* ACTION BAR */}
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={sendWhatsApp}
              className="bg-zoddle-green text-white text-sm font-semibold px-3 py-2 rounded-lg">
              📲 Send on WhatsApp
            </button>
            <button onClick={downloadImages} disabled={!selected.length}
              className="bg-zoddle-blue text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-40">
              ⬇ Download {selected.length || ''} image{selected.length === 1 ? '' : 's'}
            </button>
            <label className="text-xs text-gray-600 flex items-center gap-1">
              <input type="checkbox" checked={overlay} onChange={e => setOverlay(e.target.checked)} /> Price overlay
            </label>
            <button onClick={copyMessage} className="bg-gray-100 text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg">
              {copied === 'msg' ? 'Copied ✓' : 'Copy message'}
            </button>
            <button onClick={copyPhone} className="bg-gray-100 text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg">
              {copied === 'phone' ? 'Copied ✓' : 'Copy phone'}
            </button>
            <button onClick={doMarkSent}
              className={`text-sm font-semibold px-3 py-2 rounded-lg ${sentAt ? 'bg-green-100 text-green-700' : 'bg-zoddle-pink text-white'}`}>
              {copied === 'sent' ? 'Marked ✓' : sentAt ? 'Sent ✓ (re-mark)' : 'Mark sent'}
            </button>
            <span className="text-xs text-gray-400 ml-auto">{selected.length}/{MAX_SELECT} selected</span>
          </div>
          <div className="text-xs text-gray-400 -mt-2">
            Flow: pick 5 → Download images → Send on WhatsApp (message pre-fills) → attach the images → Mark sent.
          </div>

          {/* message */}
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
            className="w-full border rounded-lg p-3 text-sm" />

          {/* selected tray — shows the exact image that will be sent (overlay preview) */}
          {selected.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-1">
                Preview {overlay ? '(with brand + price overlay)' : '(no overlay)'}{rendering ? ' · rendering…' : ''}
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {selected.map(d => {
                  const pv = previews[d.design_id];
                  const src = (overlay && pv && pv.dataUrl) ? pv.dataUrl : cldThumb(d.image_url_1, 300);
                  return (
                    <div key={d.design_id} className="text-center">
                      <div className="relative">
                        <img src={src} alt="" className="w-full aspect-square object-cover rounded-lg border" />
                        <button onClick={() => toggle(d)} className="absolute -top-1 -right-1 bg-zoddle-pink text-white rounded-full w-5 h-5 text-xs">×</button>
                        {overlay && pv && pv.failed && (
                          <span className="absolute bottom-1 left-1 bg-amber-500 text-white text-[9px] px-1 rounded">overlay n/a</span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-600 mt-0.5 truncate">{d.brand_name}</div>
                      <div className="text-[10px] font-bold">
                        ₹{ourPrice(d)}{ourPrice(d) < Number(d.zoddle_price) ? <span className="text-gray-400 line-through ml-1">₹{d.zoddle_price}</span> : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── image browsing (bottom) ── */}
          <div className="border-t pt-3">
            <div className="flex gap-4 border-b mb-3">
              {['suggested', 'browse'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`pb-2 text-sm font-semibold ${tab === t ? 'text-zoddle-green border-b-2 border-zoddle-green' : 'text-gray-400'}`}>
                  {t === 'suggested' ? 'Suggested (20)' : 'Browse catalog'}
                </button>
              ))}
            </div>

            {tab === 'suggested' && (
              suggLoading ? <div className="py-10 text-center text-gray-400">Loading…</div>
                : sugg.length ? grid(sugg)
                : <div className="py-10 text-center text-gray-400">No in-stock designs match this child's size/gender.</div>
            )}

            {tab === 'browse' && (
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <select value={bf.gender} onChange={e => setBf({ ...bf, gender: e.target.value })} className="border rounded px-2 py-1 text-xs">
                    <option value="">Any gender</option><option value="girl">Girl</option><option value="boy">Boy</option><option value="unisex">Unisex</option>
                  </select>
                  <select value={bf.size} onChange={e => setBf({ ...bf, size: e.target.value })} className="border rounded px-2 py-1 text-xs">
                    <option value="">Any size</option>{sizes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={bf.category} onChange={e => setBf({ ...bf, category: e.target.value })} className="border rounded px-2 py-1 text-xs">
                    <option value="">Any category</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={bf.use_case} onChange={e => setBf({ ...bf, use_case: e.target.value })} className="border rounded px-2 py-1 text-xs">
                    <option value="">Any use-case</option>{useCases.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <input value={bf.brand} onChange={e => setBf({ ...bf, brand: e.target.value })} placeholder="Brand…" className="border rounded px-2 py-1 text-xs w-24" />
                  <input value={bf.q} onChange={e => setBf({ ...bf, q: e.target.value })} onKeyDown={e => e.key === 'Enter' && loadBrowse(true)} placeholder="Search…" className="border rounded px-2 py-1 text-xs w-28" />
                  <button onClick={() => loadBrowse(true)} className="bg-gray-800 text-white text-xs px-3 py-1 rounded">Search</button>
                </div>
                {bLoading && !bItems.length ? <div className="py-10 text-center text-gray-400">Loading…</div> : grid(bItems)}
                {bItems.length < bTotal && (
                  <div className="text-center mt-4">
                    <button onClick={() => loadBrowse(false)} className="text-sm text-zoddle-green font-semibold">Load more ({bItems.length}/{bTotal})</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function Messaging() {
  const [carts, setCarts] = useState([]);
  const [meta, setMeta] = useState({ count: 0, generated_at: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(3);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [search, setSearch] = useState('');
  const [hideSent, setHideSent] = useState(false);
  const [visible, setVisible] = useState(20);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = (custom.from || custom.to)
        ? { date_from: custom.from || undefined, date_to: custom.to || undefined }
        : { days };
      const res = await getAbandonedCarts(params);
      setCarts(res.carts || []);
      setMeta({ count: res.count, generated_at: res.generated_at });
      setVisible(20);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load abandoned carts.');
    } finally { setLoading(false); }
  }, [days, custom]);
  useEffect(() => { load(); }, [load]);

  const patchSent = (phone, sent_at, sent_by) =>
    setCarts(prev => prev.map(c => c.phone === phone ? { ...c, sent_at, sent_by } : c));

  const handleMark = async (c) => {
    const res = await markSent({ phone: c.phone });
    patchSent(c.phone, res.sent_at, res.sent_by);
  };
  const handleUnmark = async (c) => {
    await unmarkSent(c.phone);
    patchSent(c.phone, null, null);
  };

  const filtered = useMemo(() => {
    let list = carts;
    if (hideSent) list = list.filter(c => !c.sent_at);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(c => (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q));
    return list;
  }, [carts, search, hideSent]);

  const copyPhone = (p) => { try { navigator.clipboard.writeText(p); } catch {} };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-gray-900">Messaging</h1>
        <div className="flex items-center gap-2">
          {[3, 7, 14].map(d => (
            <button key={d} onClick={() => { setCustom({ from: '', to: '' }); setDays(d); }}
              className={`text-xs px-3 py-1 rounded-full border ${!custom.from && !custom.to && days === d ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-300 text-gray-600'}`}>
              {d}d
            </button>
          ))}
          <input type="date" value={custom.from} onChange={e => setCustom({ ...custom, from: e.target.value })} className="border rounded px-2 py-1 text-xs" />
          <span className="text-gray-400">–</span>
          <input type="date" value={custom.to} onChange={e => setCustom({ ...custom, to: e.target.value })} className="border rounded px-2 py-1 text-xs" />
          <button onClick={load} className="bg-gray-800 text-white text-xs px-3 py-1 rounded">Refresh</button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Gave a size + style but never booked. Hidden until 15 min after last activity; already-booked numbers excluded.
      </p>

      <div className="flex items-center gap-3 mb-3">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / phone…" className="border rounded px-3 py-1.5 text-xs w-60" />
        <label className="text-xs text-gray-600 flex items-center gap-1">
          <input type="checkbox" checked={hideSent} onChange={e => setHideSent(e.target.checked)} /> Hide sent
        </label>
        <span className="text-xs text-gray-500 ml-auto">
          {loading ? 'Loading…' : `${Math.min(visible, filtered.length)} of ${filtered.length} shown`}
          {meta.generated_at && !loading && <span className="text-gray-400"> · as of {fmtDate(meta.generated_at)}</span>}
        </span>
      </div>

      {error && <div className="bg-red-50 text-red-700 text-xs p-3 rounded mb-3">{error}</div>}

      <div className="overflow-x-auto bg-white rounded-lg border">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 text-[10px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Engaged</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Pincode</th>
              <th className="text-left px-3 py-2">Child(ren)</th>
              <th className="text-left px-3 py-2">Raw style</th>
              <th className="text-left px-3 py-2">Inferred</th>
              <th className="text-left px-3 py-2">Sent</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.slice(0, visible).map((c) => (
              <tr key={c.phone} className={`hover:bg-gray-50 ${c.sent_at ? 'bg-green-50/40' : ''}`}>
                <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmtDate(c.last_engagement)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium text-gray-800">{c.name || <span className="text-gray-300">—</span>}</div>
                  <button onClick={() => copyPhone(c.phone)} className="text-[11px] text-gray-500 hover:text-zoddle-green" title="Copy">{c.phone} ⧉</button>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.pincode
                    ? <Pill tone={c.serviceable ? 'green' : 'red'}>{c.pincode}{c.serviceable ? '' : ' · Unserv.'}</Pill>
                    : <Pill tone="amber">No pincode</Pill>}
                </td>
                <td className="px-3 py-2">
                  {c.children.map((k, i) => <div key={i} className="whitespace-nowrap text-gray-700">{k.gender || '?'} · {k.age_label}</div>)}
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-600 max-w-[16rem]">
                  {c.styles_raw && <div><span className="text-gray-400">Style:</span> {c.styles_raw}</div>}
                  {c.brands && <div className="truncate"><span className="text-gray-400">Brands:</span> {c.brands}</div>}
                  {c.price_range && <div><span className="text-gray-400">Budget:</span> {c.price_range}</div>}
                </td>
                <td className="px-3 py-2 text-[11px] max-w-[11rem]">
                  <div className="text-gray-700">{c.occasions.join(', ') || <span className="text-gray-300">—</span>}</div>
                  {c.categories.length > 0 && <div className="text-gray-400">{c.categories.join(', ')}</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.sent_at
                    ? <div>
                        <Pill tone="green">Sent</Pill>
                        <div className="text-[10px] text-gray-400">{fmtDate(c.sent_at)}{c.sent_by ? ` · ${c.sent_by}` : ''}</div>
                        <button onClick={() => handleUnmark(c)} className="text-[10px] text-gray-400 underline">undo</button>
                      </div>
                    : <button onClick={() => handleMark(c)} className="text-[11px] text-zoddle-blue underline">Mark sent</button>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setActive(c)} disabled={c.available_designs === 0}
                    className="bg-zoddle-green text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg disabled:opacity-40">
                    Prepare
                  </button>
                </td>
              </tr>
            ))}
            {!loading && !filtered.length && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">No abandoned carts in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {visible < filtered.length && (
        <div className="text-center mt-3">
          <button onClick={() => setVisible(v => v + 20)} className="text-sm text-zoddle-green font-semibold">
            Show more ({filtered.length - visible} more)
          </button>
        </div>
      )}

      {active && <PickerModal cart={active} onClose={() => setActive(null)} onSent={patchSent} />}
    </div>
  );
}
